// One document lifetime is one client session; navigation/remounts do not reset it.
// No backend telemetry, credentials, permission persistence, or registration writes.
import { getPushPermissionState } from './pushNotifications';
const session = `${Date.now()}-${Math.random()}`;
const WEEK = 7 * 24 * 60 * 60 * 1000;
const dismissals = new Map();
const contextualShown = new Set();
let context = null, generation = 0, pending = null, activeActions = new Set();
export const PUSH_PROMPT_EVENT = 'openrunsusa:open-push-prompt';
const notify = () => window.dispatchEvent(new Event(PUSH_PROMPT_EVENT));
const native = () => {
  try { return window.Capacitor?.getPlatform?.() === 'ios' && window.Capacitor?.isNativePlatform?.() === true; }
  catch { return false; }
};
export async function setPushPromptAccount(account, token) {
  const g = ++generation;
  context = null; pending = null; activeActions = new Set();
  if (!account || !token || !native()) return;
  try {
    const identity = await window.Capacitor.Plugins.Device.getId();
    const device = identity?.identifier || identity?.uuid;
    if (g !== generation || !device) return;
    const key = `push_reminder_v1:${JSON.stringify([account, device])}`;
    const first = localStorage.getItem(`${key}:first_session`);
    if (!first) localStorage.setItem(`${key}:first_session`, session);
    context = { account, token, key, generation: g, first: !first || first === session };
    notify();
  } catch { /* Missing identity or storage fails closed. */ }
}
export function clearPushPromptAccount() { ++generation; context = null; pending = null; activeActions = new Set(); }
export function beginPushPromptAction() {
  const action = {}, owner = context; activeActions.add(action); notify();
  const finish = () => { activeActions.delete(action); notify(); };
  finish.accepted = () => { if (owner && context === owner) askAfterAcceptInvite(owner.account, owner.token); };
  finish.joined = () => { if (owner && context === owner) askAfterJoinRun(owner.account, owner.token); };
  return finish;
}
export function askAfterAcceptInvite(account, token) {
  if (!context || (account && context.account !== account) || (token && context.token !== token)) return false;
  pending = { generation: context.generation, source: 'accept_invite' }; notify(); return true;
}
export function askOnMyFeed() {
  if (!context) return false;
  if (!pending) pending = { generation: context.generation, source: 'my_feed' };
  notify(); return true;
}
// Retain the existing exports; reminder timing is acceptance or subsequent My Feed.
export function askAfterJoinRun(account, token) {
  if (!context || (account && context.account !== account) || (token && context.token !== token)) return false;
  pending = { generation: context.generation, source: 'join' }; notify(); return true;
}
export function askAfterCreateRun() { return false; }
export async function eligiblePushReminder(myFeed = false) {
  const c = context;
  if (c && myFeed && !pending) pending = { generation: c.generation, source: 'my_feed' };
  if (!c || c.first || activeActions.size || pending?.generation !== c.generation) return null;
  // My Feed fallback cannot authorize a reminder on Game Detail.
  if (!myFeed && !['accept_invite', 'join'].includes(pending.source)) return null;
  const source = pending.source;
  const contextual = source !== 'my_feed';
  try {
    const last = dismissals.get(c.key) ?? localStorage.getItem(`${c.key}:dismissed`);
    if (contextual ? contextualShown.has(c.key) : last !== null && (!Number.isFinite(Number(last)) || Date.now() - Number(last) < WEEK)) return null;
    const permission = await getPushPermissionState();
    if (context !== c || activeActions.size || pending?.source !== source) return null;
    if (!(contextual ? permission === 'prompt' : ['prompt', 'denied'].includes(permission))) {
      if (contextual) pending = null;
      return null;
    }
    return { ...c, permission, source };
  } catch { return null; }
}
export function pushReminderCurrent(c) { return context?.generation === c?.generation && !activeActions.size; }
export function recordPushReminderShown(c) {
  if (!pushReminderCurrent(c)) return;
  if (c.source !== 'my_feed') { contextualShown.add(c.key); return; }
  dismissals.set(c.key, String(Date.now()));
  try { localStorage.setItem(`${c.key}:dismissed`, String(Date.now())); } catch { /* no persistence available */ }
}
export function markDismissed(c) { if (pushReminderCurrent(c)) { recordPushReminderShown(c); pending = null; } }
export function consumePushReminder(c) { if (pushReminderCurrent(c)) pending = null; }
